import { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext.jsx';
import NotificationBell from '../NotificationBell.jsx';
import MobileNav from './MobileNav.jsx';
import plusNav from '../../plus/plusNav.js';

/**
 * The one top bar every authenticated page renders: brand (-> dashboard), the
 * module menu, notifications and sign-out. It is sticky (see `.app-header` in
 * index.css), so it stays on screen while the page scrolls and any module is
 * one click away. On phones the menu collapses into the MobileNav drawer.
 *
 * `match` is the path prefix that lights a link up as the current module when
 * it differs from the link target (Approvals lives under /workflows).
 */
const MODULES = [
  { to: '/hr', label: 'HR' },
  { to: '/finance', label: 'Finance' },
  { to: '/procurement', label: 'Procurement' },
  { to: '/pm', label: 'Projects' },
  { to: '/wiki', label: 'Wiki', testid: 'nav-wiki-link' },
  { to: '/crm', label: 'CRM' },
  { to: '/dms', label: 'Documents' },
  { to: '/messenger', label: 'Messenger', testid: 'nav-messenger-link' },
  { to: '/analytics', label: 'Analytics' },
  { to: '/events', label: 'Events' },
  { to: '/admin', label: 'Admin' },
  { to: '/workflows/my-approvals', label: 'Approvals', match: '/workflows' },
];

const under = (pathname, prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`);

export default function AppHeader() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();

  // "All modules" is current on the launcher and inside any expansion module, but
  // never alongside a top-level link (Messenger is both) — one highlight per page.
  const inTopLevel = MODULES.some((m) => under(pathname, m.match || m.to));
  const inPlus = !inTopLevel && (under(pathname, '/plus') || plusNav.some((n) => under(pathname, n.path)));

  // On narrow screens the menu scrolls sideways; keep the current module in view.
  const navRef = useRef(null);
  useEffect(() => {
    navRef.current?.querySelector('a.active')?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, [pathname]);

  return (
    <header className="app-header">
      <MobileNav />
      <div className="brand"><Link to="/dashboard">BOS<span>+</span></Link></div>
      <nav className="app-topnav" aria-label="Modules" ref={navRef}>
        {MODULES.map((m) => {
          const current = under(pathname, m.match || m.to);
          return (
            <Link
              key={m.to}
              to={m.to}
              className={current ? 'active' : undefined}
              aria-current={current ? 'page' : undefined}
              data-testid={m.testid}
            >
              {m.label}
            </Link>
          );
        })}
        <Link
          to="/plus"
          className={inPlus ? 'active' : undefined}
          aria-current={inPlus ? 'page' : undefined}
        >
          All modules
        </Link>
      </nav>
      <div className="app-header-actions">
        <NotificationBell />
        <span className="muted app-header-email">{user?.email}</span>
        <button type="button" className="btn ghost" onClick={logout} title={user?.email}>Sign out</button>
      </div>
    </header>
  );
}
